import React, { useState, useEffect } from "react";
import ProgressBar from "./ProgressBar";
import { post } from "../../../services/ApiService";
import toast, { Toaster } from "react-hot-toast";

const parseJobErrors = (errorLogs) => {
  if (!errorLogs) return [];

  let parsed;
  try {
    parsed = JSON.parse(errorLogs);
  } catch {
    return typeof errorLogs === "string" && errorLogs.trim()
      ? [{ message: errorLogs }]
      : [];
  }

  if (typeof parsed === "string") return parsed.trim() ? [{ message: parsed }] : [];

  // Amazon: array of { orderReference, errorMessage }
  if (Array.isArray(parsed)) {
    return parsed
      .flatMap((e) => {
        if (e?.orderReference && e?.errorMessage) {
          return [{ message: `${e.orderReference}: ${e.errorMessage}` }];
        }

        if (e?.order?.orderReference && Array.isArray(e?.errors)) {
          return e.errors
            .map((errorEntry) => errorEntry?.errorMessage)
            .filter(Boolean)
            .map((errorMessage) => ({
              message: `${e.order.orderReference}: ${errorMessage}`,
            }));
        }

        const fallbackMessage = e?.errorMessage || e?.message;
        return fallbackMessage ? [{ message: fallbackMessage }] : [];
      })
      .filter((e) => e.message);
  }

  // Royal Mail: { Parcel: "...", Letter: "...", "Large letter": "...", undefined: "..." }
  if (typeof parsed === "object") {
    return Object.entries(parsed)
      .filter(([, v]) => v && typeof v === "string" && v.trim())
      .map(([k, v]) => ({ message: `${k}: ${v}` }));
  }

  return [];
};

const JobCard = ({ batch, Active }) => {
  const [labels, setLabels] = useState([]);
  const [showStats, setShowStats] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState({});
  const [clearingJobs, setClearingJobs] = useState({});
  useEffect(() => {
    const allLabels = (batch.jobs || []).flatMap((job) =>
      (job.labels || []).map((label) => ({
        ...label,
        jobId: job._id,
        domain: job.domain,
      }))
    );
    setLabels(allLabels);
  }, [batch]);

  const handleLabelClick = async (label) => {
    if (!label) return;

    // If already printed, ask for confirmation
    if (label.isPrinted) {
      const confirmRedownload = window.confirm(
        "This label is already printed. Do you want to print it again?"
      );
      if (!confirmRedownload) return;
    }

    try {
      setLoadingLabels((prev) => ({ ...prev, [label.filePath]: true }));
      const baseUrl =
        label.domain === "external"
          ? process.env.REACT_APP_EXT_API_ENDPOINT
          : process.env.REACT_APP_API_ENDPOINT;

      const apiUrl = `${baseUrl}/api/orders/label/download`;
      const response = await fetch(apiUrl, {
        method: "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ filePath: label.filePath }),
      });

      console.log(response);

      const blob = await response.blob();
      await downloadFile(blob, label?.fileName);

      // ✅ Mark as printed if not already
      if (!label.isPrinted) {
        const payload = { jobId: label.jobId, filePath: label.filePath };
        const printRes = await post(`api/orders/labels/mark-printed`, payload);
        if (printRes.success) {
          setLabels((prev) =>
            prev.map((l) =>
              l.filePath === label.filePath ? { ...l, isPrinted: true } : l
            )
          );
        } else {
          toast.error("Failed to mark label as printed");
        }
      }
    } catch (err) {
      console.error("Download failed:", err.message);
      toast.error("Download failed, please try again.");
    } finally {
      setLoadingLabels((prev) => ({ ...prev, [label.filePath]: false }));
    }
  };


  const downloadFile = async (blob, fileName = "shipping-label") => {
    try {
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      throw err; // let caller handle error
    }
  };

  const handleClearJob = async (jobId) => {
    try {
      setClearingJobs((prev) => ({ ...prev, [jobId]: true }));
      const res = await post("api/orders/courier/jobs/clear", { jobId });
      if (res.success) {
        toast.success("Job cleared successfully");
      } else {
        toast.error(res.error || "Failed to clear job");
      }
    } catch (err) {
      console.error("Clear failed:", err);
      toast.error("Failed to clear job");
    } finally {
      setClearingJobs((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  const renderStats = (job) => {
    if (!job.stats) return null;

    switch (job.type) {
      case "RM_ORDERS":
        return (
          <div className="mt-3">
            <div className="row small">
              {Object.entries(job.stats || {}).map(([format, stats]) => (
                <div key={format} className="col-12 mb-2">
                  <div className="d-flex justify-content-between">
                    <span className="text-capitalize fw-bold text-white">
                      {format}:
                    </span>
                    <span className="text-white">
                      <span className="text-success">{stats.created}✔</span> /
                      <span className="text-danger"> {stats.failed}✖</span> /
                      <span className=""> {stats.total}↻</span>
                    </span>
                  </div>
                  {stats.error && stats.error.trim() !== "" && (
                    <div className="text-white bg-danger p-4 small mt-1">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {stats.error.length > 50 ? `${stats.error}` : stats.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case "AMAZON_ORDERS":
        return (
          <div className="mt-3">
            <div className="row small">
              {job.stats.undefined && job.stats.undefined.total > 0 && (
                <div className="col-12 mb-2">
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold text-white">Undefined:</span>
                    <span className="text-white d-inline-block mb-2">
                      <span className="bg-success d-inline-block px-2 me-1" title="Created Orders">
                        {job.stats.undefined.created}✔
                      </span>{" "}
                      /
                      <span className="bg-danger d-inline-block px-2 ms-3 me-1" title="Failed Orders">
                        {" "}
                        {job.stats.undefined.failed}✖
                      </span>{" "}
                      /
                      <span className="d-inline-block px-2" title="Total Orders">
                        {" "}
                        {job.stats.undefined.total}↻
                      </span>
                    </span>
                  </div>
                  {job.stats.undefined.error && (
                    <div className="text-white bg-danger p-4 small mt-1">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {job.stats.undefined.error.length > 50
                        ? `${job.stats.undefined.error}`
                        : job.stats.undefined.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div
        className="card"
        style={{ background: "#38485fff", borderRadius: "5px", opacity: "0.9" }}
      >
        <div className="card-body">
          {/* loop through jobs inside this batch */}
          <span className="badge bg-primary mb-4 fw-bold text-uppercase">
            {batch.jobs[0].domain}
          </span>
          {batch.jobs.map((job, idx) => {
            const jobErrors = parseJobErrors(job.errorLogs);
            return (
            <div
              key={idx}
              className=""
              style={{ border: "1px dashed", padding: "5px" }}
            >
              {jobErrors.length > 0 && (
                <div
                  className="mb-3"
                  style={{
                    background: "#fff2f0",
                    border: "1px solid #ffccc7",
                    borderRadius: "8px",
                    padding: "12px",
                    color: "#7a1f1f",
                  }}
                >
                  <div className="fw-bold mb-2">
                    <i className="bi bi-exclamation-triangle me-2" />
                    Action required
                  </div>
                  {jobErrors.map((e, i) => (
                    <div key={i} style={{ fontSize: "13px", marginTop: i > 0 ? 6 : 0 }}>
                      {e.message}
                    </div>
                  ))}
                </div>
              )}
              <div className="text-white mb-3" style={{ fontSize: "16px" }}>
                {/* === Row 1: summary line with stats toggle === */}

                <div className="row align-items-center">
                  <div className="col-auto">
                    <strong>Total:</strong> {job.totalOrders}
                  </div>
                  <div className="col-auto">
                    <strong>Failed:</strong>{" "}
                    <span className="text-white bg-danger px-4 rounded-2 d-inline-block">{job.failed}</span>
                  </div>
                  <div className="col-auto">
                    <strong>Started:</strong>{" "}
                    {new Date(job.startedAt).toLocaleTimeString()}
                  </div>
                  <div className="col text-end">
                    <strong>
                      {job.created}/{job.totalOrders}
                    </strong>{" "}
                    {job.totalOrders > 0 &&
                      `${Math.round((job.created / job.totalOrders) * 100)}%`}
                    {job.stats && (
                      <i
                        className={`bi bi-chevron-${showStats ? "up" : "down"
                          } ms-2`}
                        style={{ cursor: "pointer", color: "white" }}
                        onClick={() => setShowStats(!showStats)}
                      />
                    )}
                  </div>
                </div>

                {/* === Row 2: expanded stats === */}
                {showStats && job.stats && (
                  <div className="row mt-2">
                    <div className="col">{renderStats(job)}</div>
                  </div>
                )}
              </div>

              {job.status === "IN_PROGRESS" && (
                <ProgressBar
                  value={job.created}
                  max={job.totalOrders}
                  color={"#e2f01bff"}
                />
              )}

              {job.failed > 0 && job.status !== "CLEARED" && (
                <button
                  className="btn btn-sm btn-outline-light mt-2"
                  disabled={clearingJobs[job._id]}
                  onClick={() => handleClearJob(job._id)}
                >
                  {clearingJobs[job._id] ? (
                    <>
                      <i className="bi bi-hourglass-split me-1" />
                      Clearing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-1" />
                      Clear
                    </>
                  )}
                </button>
              )}
            </div>
            );
          })}

          {/* combined labels for the batch */}
          {labels.length > 0 && (
            <div className="mt-6">
              <h6
                className="text-uppercase mt-4"
                style={{ color: "white", fontSize: "15px" }}
              >
                <i className="bi bi-tags me-1"></i>All Labels
              </h6>
              <div className="d-flex flex-column gap-2">
                {labels.map((label, idx) => {
                  const isLoading = loadingLabels[label.filePath];

                  return (
                    <div
                      key={idx}
                      className="d-flex justify-content-between align-items-center mb-2"
                      style={{
                        background: label.isPrinted ? "#e8f5e9" : "#2a3a57",
                        border: label.isPrinted
                          ? "1px solid #4caf50"
                          : "1px solid #3f51b5",
                        borderRadius: "8px",
                        padding: "2px 16px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        opacity: isLoading ? 0.6 : 1,
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                      onClick={() => handleLabelClick(label)}
                      role="button"
                      aria-disabled={isLoading}
                    >
                      <div className="d-flex align-items-center">
                        {isLoading ? (
                          <i className="bi bi-hourglass-split me-2 text-warning" />
                        ) : (
                          <i
                            className={`bi bi-file-earmark-pdf me-2 ${label.isPrinted ? "text-dark" : "text-white"
                              }`}
                            style={{ fontSize: "20px" }}
                          />
                        )}
                        <span
                          className={
                            label.isPrinted
                              ? "text-dark fw-semibold"
                              : "text-white fw-semibold"
                          }
                          style={{ fontSize: "14px" }}
                        >
                          {label?.fileName || label?.filePath}
                        </span>
                      </div>

                      <span
                        style={{
                          background: label.isPrinted ? "#4caf50" : "#ffc107",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: "bold",
                          borderRadius: "12px",
                          padding: "4px 10px",
                          letterSpacing: "0.3px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isLoading
                          ? "Downloading..."
                          : label.isPrinted
                            ? "PRINTED"
                            : "READY TO PRINT"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JobCard;
